import React, { useState, useEffect, useRef } from "react";
import style from "./SlidingTab.module.scss";
import useCommonStore from "../../stores/common";
import useDomainStore from "../../stores/domain";

/**
 * SlidingTab component that displays a set of tabs with a sliding background indicator
 * @component
 * @param {Object} props - Component props
 * @param {string[]} props.options - Array of tab options to display
 * @param {Function} [props.onChange] - Callback function called when a tab is selected
 * @returns {JSX.Element} SlidingTab component
 */
function SlidingTab({
  options,
  onChange,
  selectedTab = "",
  fullWidth = false,
  noShortCodes = false,
  noShrink = false,
}) {
  const { isKurvDomain } = useDomainStore();
  // State for active tab and background animation
  const [activeTab, setActiveTab] = useState(selectedTab || options[0]);
  const [backgroundWidth, setBackgroundWidth] = useState(0);
  const [backgroundTransform, setBackgroundTransform] =
    useState("translateX(0)");

  // Refs for tab elements to calculate positions
  const tabRefs = useRef([]);

  // Store state for UI expansion
  const expandDetails = useCommonStore((state) => state.expandDetails);

  /**
   * Mapping of tab names to their short codes
   * Used when expandDetails is false to show abbreviated versions
   */
  const shortCodes = {
    Underwriting: "UW",
    All: "AL",
    PCI: "P",
    Agent: "A",
    Merchant: "M",
  };

  /**
   * Effect to update background position and width when active tab changes
   * Calculates the position and width of the active tab for the sliding animation
   */
  useEffect(() => {
    if (tabRefs.current.length > 0) {
      const activeTabElement = tabRefs.current[options.indexOf(activeTab)];
      if (activeTabElement) {
        setBackgroundWidth(activeTabElement.offsetWidth);
        setBackgroundTransform(`translateX(${activeTabElement.offsetLeft}px)`);
      }
    }
  }, [activeTab, options]);

  /**
   * Handles tab selection
   * Updates active tab and calls onChange callback if provided
   * @param {string} tab - The selected tab name
   */
  const handleClick = (tab) => {
    setActiveTab(tab);
    if (onChange) {
      onChange(tab);
    }
  };

  return (
    <div
      className={`${style.tabContainer} ${fullWidth ? style.fullWidth : ""} ${isKurvDomain ? style.kurv_theme : ""}`}
    >
      <div className={style.tabList}>
        {options.map((tab, index) => (
          <button
            key={tab}
            type="button"
            ref={(el) => (tabRefs.current[index] = el)}
            className={`${style.tab} ${
              activeTab === tab ? style.activeTab : ""
            } ${noShrink ? style.noShrink : ""}`}
            onClick={() => handleClick(tab)}
            title={!expandDetails && shortCodes[tab] ? tab : ""}
          >
            {!expandDetails && !noShortCodes && shortCodes[tab]
              ? shortCodes[tab]
              : tab}
          </button>
        ))}
        <div
          className={style.activeBackground}
          style={{
            width: backgroundWidth,
            transform: backgroundTransform,
          }}
        />
      </div>
    </div>
  );
}

export default SlidingTab;
